import { ArrowLeftOutlined } from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import {
    Card, Col, Flex, Row, theme, Typography,
} from 'antd';
import {
    useCallback, useState,
} from 'react';
import { Helmet } from 'react-helmet';
import {
    Link,
    useNavigate,
} from 'react-router-dom';

import { objectionsMutations } from '@/api';
import {
    ConfirmModal, getActionControlStyleVars, ObjectionForm,
} from '@/components';
import { AppRoute } from '@/constants';
import { useBackToOrList } from '@/hooks';

export const ObjectionCreatePage = () => {
    const navigate = useNavigate();
    const handleBack = useBackToOrList(AppRoute.Objections);
    const { token } = theme.useToken();
    const [confirmModalOpen, setConfirmModalOpen] = useState(false);
    const [formData, setFormData] = useState<{
        title: string;
        body?: string | null;
        priority?: number | null;
    } | null>(null);

    const createMutationOptions = objectionsMutations.create();
    const { mutate: createObjection, isPending } = useMutation(createMutationOptions);

    const handleSubmit = useCallback((data: {
        title: string;
        body?: string | null;
        priority?: number | null;
    }) => {
        setFormData(data);
        setConfirmModalOpen(true);
    }, []);

    const handleConfirm = useCallback(() => {
        if (formData) {
            createObjection(formData, {
                onSuccess: () => {
                    setConfirmModalOpen(false);
                    setFormData(null);
                    navigate(AppRoute.Objections);
                },
            });
        }
    }, [formData, createObjection, navigate]);

    const handleCancel = useCallback(() => {
        handleBack();
    }, [handleBack]);

    const handleModalCancel = useCallback(() => {
        setConfirmModalOpen(false);
        setFormData(null);
    }, []);

    return (
        <>
            <Helmet>
                <title>Створити заперечення</title>
            </Helmet>

            <Row justify="center">
                <Col xs={24} sm={24} md={20} lg={16} xl={12}>
                    <Card
                        title={
                            <Flex align="center" gap="middle">
                                <Link
                                    to={AppRoute.Objections}
                                    aria-label="Назад"
                                    className="app-action-control app-action-control--icon"
                                    style={getActionControlStyleVars(token, {
                                        variant: 'text',
                                        bordered: false,
                                        iconOnly: true,
                                    })}
                                >
                                    <ArrowLeftOutlined style={{ fontSize: 16 }} />
                                </Link>
                                <Typography.Title level={4} style={{ margin: 0 }}>
                                    Створити заперечення
                                </Typography.Title>
                            </Flex>
                        }
                    >
                        <ObjectionForm
                            onSubmit={handleSubmit}
                            onCancel={handleCancel}
                            loading={isPending}
                            cancelButtonText="Відмінити"
                            cancelTo={AppRoute.Objections}
                        />
                    </Card>
                </Col>
            </Row>

            <ConfirmModal
                open={confirmModalOpen}
                mode="create"
                entityType="objection"
                entityName={formData?.title}
                onConfirm={handleConfirm}
                onCancel={handleModalCancel}
                loading={isPending}
            />
        </>
    );
};
